/**
 * Pure identity-resolution helpers for the discount migration: legacy portal ids and
 * organizations to their cloud-core-api / cds counterparts.
 *
 * Deliberately free of any import from `src/api.js` — that module imports the app entry
 * (`./index.js`) to reach the Redux store, so anything touching it pulls the whole component
 * tree into a unit test (and blows up on `REACT_APP_CURRENCIES` being parsed at import time).
 * Keeping these here lets the thunks stay thin and these rules stay directly testable.
 */

/**
 * Build a bidirectional legacy-id ↔ cds-uuid map from core-api rows.
 *
 * Rows whose legacy key is missing are counted rather than dropped quietly: a product type with
 * no `sourceExternalId`, or a facility with no `reference`, cannot be targeted from the portal
 * at all, and that has to be visible.
 *
 * @param {Array<object>} rows flattened `{id, ...attributes}` rows
 * @param {string} legacyKey attribute holding the legacy id
 * @returns {{toCds: object, toLegacy: object, unkeyed: number, total: number}}
 */
export function buildMap(rows, legacyKey) {
    const toCds = {};
    const toLegacy = {};
    let unkeyed = 0;

    (rows || []).forEach(row => {
        const legacyId = row && row[legacyKey];
        if (!legacyId || !row.id) {
            unkeyed += 1;
            return;
        }
        toCds[legacyId] = row.id;
        toLegacy[row.id] = legacyId;
    });

    return { toCds, toLegacy, unkeyed, total: (rows || []).length };
}

/**
 * Match a portal organization against a cloud-core-api merchant list.
 *
 * Tried in order: exact `name`, case-insensitive `name`, then `urlname`. Returns null rather
 * than guessing — this is the one part of the merchant plumbing with no authoritative mapping
 * behind it (`user.group.Name` and the merchant list come from different reads), so it should be
 * asserted against an organization where `name !== urlname`.
 *
 * Note the caller must then use `merchant.urlname` for every cds and `/data/*` path — the
 * matched `name` is only used to find the row.
 *
 * @param {Array<object>} merchants rows from `newCoreApi.fetchMerchants`
 * @param {object} group `user.group` — `{Id, Name}`
 * @returns {object|null}
 */
export function matchMerchant(merchants, group) {
    const name = group && group.Name;
    if (!Array.isArray(merchants) || !name) {
        return null;
    }
    const lowered = name.toLowerCase();
    return merchants.find(m => m.name === name)
        || merchants.find(m => typeof m.name === 'string' && m.name.toLowerCase() === lowered)
        || merchants.find(m => typeof m.urlname === 'string' && m.urlname.toLowerCase() === lowered)
        || null;
}

/**
 * Find the `merchant_user` row for a legacy Selfly userKey.
 *
 * The userKey lives in a different column depending on how the row was created:
 *
 * | user type                 | `userId`        | `cardAlias`     | `reference`      |
 * |---------------------------|-----------------|-----------------|------------------|
 * | keycloak (husky-migrated) | **the userKey** | husky's value   | husky's value    |
 * | bankcard (sync-minted)    | derived uuid    | **the userKey** | null             |
 * | orphan (legacy rows only) | derived uuid    | null            | **the userKey**  |
 *
 * So match in the direction where the key is the **input** — that is unambiguous and needs no
 * inference. Do NOT discriminate on `backendType` or `userType`: both are copied from husky
 * (`HuskyUserHandler:62,69`), so `backendType === 'SELFLY'` means "belongs to a Selfly-backed
 * merchant", not "sync-minted" — a keycloak user of a Selfly merchant carries it too.
 *
 * @param {Array<object>} merchantUsers rows from `newCoreApi.fetchMerchantUsers`
 * @param {string} userKey legacy Selfly userKey
 * @returns {Array<object>} every match; more than one means a husky column collided with
 *          another user's Selfly key — unlikely, but the caller must surface it, not pick one
 */
export function matchMerchantUser(merchantUsers, userKey) {
    if (!Array.isArray(merchantUsers) || !userKey) {
        return [];
    }
    return merchantUsers.filter(u => u
        && (u.userId === userKey || u.cardAlias === userKey || u.reference === userKey));
}

/**
 * Build the userKey ↔ `merchant_user.id` maps from a userKey list and the merchant-user rows.
 *
 * Driven by the known userKeys (the portal already fetches them for its picker) rather than by
 * deriving a key from each merchant_user row. `ambiguous` collects keys that matched more than
 * one row so the caller can refuse rather than guess.
 *
 * @param {Array<object>} merchantUsers rows from `newCoreApi.fetchMerchantUsers`
 * @param {Array<string>} userKeys legacy userKeys to resolve
 * @returns {{toCds: object, toLegacy: object, unkeyed: number, total: number, ambiguous: Array<string>}}
 */
export function buildUserMap(merchantUsers, userKeys) {
    const toCds = {};
    const toLegacy = {};
    const ambiguous = [];
    let unkeyed = 0;

    (userKeys || []).forEach(key => {
        const matches = matchMerchantUser(merchantUsers, key);
        if (matches.length === 0) {
            unkeyed += 1;
            return;
        }
        if (matches.length > 1) {
            ambiguous.push(key);
            return;
        }
        toCds[key] = matches[0].id;
        toLegacy[matches[0].id] = key;
    });

    return { toCds, toLegacy, unkeyed, total: (userKeys || []).length, ambiguous };
}

/**
 * Map each user group to its member `merchant_user.id`s: `{groupId: [merchantUserId]}`.
 *
 * A cds `USER_GROUP` target names a group, never its members, so this is the hop that makes a
 * user-restricted discount readable. Groups with no members map to `[]` rather than being dropped,
 * so the caller can tell "empty group" — a restriction matching nobody — apart from "group not
 * found", whose scope is unknowable.
 *
 * @param {Array<object>} groups rows from `newCoreApi.fetchUserGroups`
 * @returns {{members: object, total: number}}
 */
export function buildUserGroupMap(groups) {
    const members = {};
    (groups || []).forEach(group => {
        if (!group || !group.id) {
            return;
        }
        members[group.id] = (Array.isArray(group.users) ? group.users : [])
            .map(u => u && u.id)
            .filter(Boolean);
    });
    return { members, total: Object.keys(members).length };
}
